/**
 * Typed API client.
 *
 * docs/08-frontend-and-ux.md §1.2: responses are Result-shaped and RFC 9457
 * problem documents are parsed into a `Problem` surfaced by one toast/panel
 * convention. Every mutating request carries the CSRF header of docs/09 §2.4;
 * the session cookie is sent by the browser and never read by JS (ADR-0007).
 */

import {
  CSRF_HEADER,
  CSRF_HEADER_VALUE,
  type AdminUser,
  type AuditEntry,
  type CouncilRoster,
  type CouncilSeat,
  type DerivedRelationsResponse,
  type ErrorCode,
  type InviteTokenPreview,
  type IssuedInvite,
  type GalleryResponse,
  type KinshipPathResponse,
  type LineageResponse,
  type LineageSearchResponse,
  type TreeScene,
  type UploadedPhoto,
  type LoginResponse,
  type Notification,
  type CreatedPerson,
  type CreatedRelation,
  type PageMeta,
  type PendingClaim,
  type Person,
  type PersonDetail,
  type PersonHistory,
  type PersonSummary,
  type PersonWritable,
  type PhotoView,
  type Problem,
  type Proposal,
  type ProposalDetail,
  type RelationAssertion,
  type SessionUser,
  type SettingsView,
  type Vote,
  type VoteChoice,
  type VoteRow,
} from '@famtree/shared';

export const API_BASE = '/api/v1';

/** A failed request. Callers dispatch on `problem.code`, never on the text. */
export class ApiError extends Error {
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
  }
  get code(): ErrorCode {
    return this.problem.code;
  }
}

const offlineProblem = (): Problem => ({
  type: 'about:blank',
  title: 'Network unavailable',
  status: 0,
  code: 'internal',
  detail: 'The server could not be reached.',
});

function isProblem(value: unknown): value is Problem {
  return typeof value === 'object' && value !== null && 'code' in value && 'status' in value;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };

  if (method !== 'GET') {
    headers['content-type'] = 'application/json';
    // Non-simple header: cross-origin use would need a preflight the api never grants.
    headers[CSRF_HEADER] = CSRF_HEADER_VALUE;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw new ApiError(offlineProblem());
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    throw new ApiError(
      isProblem(parsed)
        ? parsed
        : {
            type: 'about:blank',
            title: response.statusText || 'Request failed',
            status: response.status,
            code: 'internal',
          },
    );
  }

  return parsed as T;
}

/* ------------------------------------------------------------------ endpoints */

export const authApi = {
  me: () => apiFetch<{ user: SessionUser }>('/auth/me'),
  login: (email: string, password: string) =>
    apiFetch<LoginResponse>('/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
  logoutAll: () => apiFetch<void>('/auth/logout-all', { method: 'POST' }),
  invitePreview: (token: string) =>
    apiFetch<InviteTokenPreview>(`/auth/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, password: string, displayName?: string) =>
    apiFetch<LoginResponse>('/auth/accept-invite', {
      method: 'POST',
      body: { token, password, ...(displayName ? { display_name: displayName } : {}) },
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<void>('/auth/password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    }),
  resetPassword: (token: string, newPassword: string) =>
    apiFetch<LoginResponse>('/auth/password-reset', {
      method: 'POST',
      body: { token, new_password: newPassword },
    }),
};

export const settingsApi = {
  get: () => apiFetch<{ settings: SettingsView }>('/settings'),
  /**
   * `expected_updated_at` is the optimistic-concurrency guard of docs/05 §9.10:
   * two admins editing the council's rules from stale forms is exactly the case
   * a last-write-wins update would silently lose.
   */
  update: (
    changes: {
      council_size?: number;
      approval_threshold?: number;
      voting_window_days?: number;
    },
    expectedUpdatedAt?: string,
  ) =>
    apiFetch<{ settings: SettingsView }>('/settings', {
      method: 'PATCH',
      body: { changes, ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}) },
    }),
};

export interface ProposalListParams {
  filter?: 'open' | 'mine' | 'needs_my_vote' | 'all';
  status?: string | string[];
  kind?: string | string[];
  subject_person_id?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
  [param: string]: string | number | string[] | undefined;
}

export const proposalsApi = {
  list: (params: ProposalListParams = {}) =>
    apiFetch<{ data: Proposal[]; page: PageMeta }>(`/proposals${queryString(params)}`),
  get: (id: string) => apiFetch<ProposalDetail>(`/proposals/${id}`),
  votes: (id: string) => apiFetch<{ data: VoteRow[]; page: PageMeta }>(`/proposals/${id}/votes`),
  /** PUT, not POST: a seat's ballot is a replaceable sub-resource (docs/05 §9.4). */
  vote: (id: string, choice: VoteChoice, comment?: string | null) =>
    apiFetch<{ vote: Vote; proposal: Proposal }>(`/proposals/${id}/vote`, {
      method: 'PUT',
      body: { choice, ...(comment ? { comment } : {}) },
    }),
  withdraw: (id: string, reason?: string | null) =>
    apiFetch<{ proposal: Proposal }>(`/proposals/${id}/withdraw`, {
      method: 'POST',
      body: reason ? { reason } : {},
    }),
};

export const usersApi = {
  /** "That person in the tree is me" — raised by the member, settled by an admin. */
  claim: (personId: string) =>
    apiFetch<{ claimed_person_id: string }>('/users/me/claim', {
      method: 'POST',
      body: { person_id: personId },
    }),
  withdrawClaim: () => apiFetch<void>('/users/me/claim', { method: 'DELETE' }),
  claims: () => apiFetch<{ data: PendingClaim[] }>('/users/claims'),
  resolveClaim: (userId: string, decision: 'approve' | 'refuse', note?: string) =>
    apiFetch<{ decision: string; person_id: string }>(`/users/${userId}/claim`, {
      method: 'POST',
      body: { decision, ...(note ? { note } : {}) },
    }),
  /** Admin-only roster; the seat picker needs it to offer eligible holders. */
  list: (
    params: { q?: string; status?: string; has_person?: 'true' | 'false'; limit?: number } = {},
  ) => apiFetch<{ data: AdminUser[]; page: PageMeta }>(`/users${queryString(params)}`),
};

export const councilApi = {
  seats: (includeHistory = false) =>
    apiFetch<CouncilRoster>(
      `/council/seats${includeHistory ? '?include_history=true&limit=100' : '?limit=100'}`,
    ),
  grant: (holderUserId: string, seatNo?: number) =>
    apiFetch<{ seat: CouncilSeat; bootstrap_mode_ended: boolean }>('/council/seats', {
      method: 'POST',
      body: { holder_user_id: holderUserId, ...(seatNo ? { seat_no: seatNo } : {}) },
    }),
  vacate: (seatId: string) => apiFetch<void>(`/council/seats/${seatId}`, { method: 'DELETE' }),
};

export const notificationsApi = {
  list: (params: { unread?: 'true' | 'false'; limit?: number; cursor?: string } = {}) =>
    apiFetch<{ data: Notification[]; page: PageMeta; unread_count: number }>(
      `/notifications${queryString(params)}`,
    ),
  markRead: (id: string) =>
    apiFetch<{ notification: Notification }>(`/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () => apiFetch<{ marked: number }>('/notifications/read-all', { method: 'POST' }),
};

export interface AuditListParams {
  action?: string;
  entity_type?: string | string[];
  entity_id?: string;
  actor_user_id?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
  [param: string]: string | number | string[] | undefined;
}

export const auditApi = {
  list: (params: AuditListParams = {}) =>
    apiFetch<{ data: AuditEntry[]; page: PageMeta }>(`/audit-log${queryString(params)}`),
};

export const invitesApi = {
  create: (email: string, displayName: string, personId?: string) =>
    apiFetch<{ user: SessionUser; invite: IssuedInvite }>('/invites', {
      method: 'POST',
      body: { email, display_name: displayName, ...(personId ? { person_id: personId } : {}) },
    }),
};

export const personsApi = {
  list: (params: PersonListParams = {}) =>
    apiFetch<{ data: PersonSummary[]; page: PageMeta }>(`/persons${queryString(params)}`),
  get: (id: string) => apiFetch<PersonDetail>(`/persons/${id}`),
  history: (id: string) => apiFetch<PersonHistory>(`/persons/${id}/history`),
  /**
   * Find someone by descent — "Hassan ben Idriss ben Mehdi".
   *
   * A separate call from `list`, not a flag on it: the answer is a *chain* per
   * match, not a row, and flattening it would throw away the only thing that
   * tells two identically-named cousins apart.
   */
  lineage: (q: string, limit = 10) =>
    apiFetch<LineageSearchResponse>(`/persons/lineage${queryString({ q, limit })}`),
  create: (body: {
    person: PersonWritable;
    relations?: unknown[];
    summary?: string;
    source_note?: string | null;
  }) => apiFetch<CreatedPerson>('/persons', { method: 'POST', body }),
  // Outside bootstrap these answer 202 with the proposal they opened, and the
  // row itself is untouched until the council approves (docs/05 §1.9).
  update: (id: string, body: { changes: Partial<PersonWritable>; summary?: string }) =>
    apiFetch<{ person: Person; proposal: Proposal | null; bootstrap: boolean }>(`/persons/${id}`, {
      method: 'PATCH',
      body,
    }),
  retire: (id: string, body: { retire_reason: string; superseded_by_id?: string | null }) =>
    apiFetch<{ person: Person; proposal: Proposal | null; bootstrap: boolean }>(`/persons/${id}`, {
      method: 'DELETE',
      body,
    }),
};

/**
 * The kinship reads (docs/05 §6.6, §7). Everything here is computed per request
 * from the graph snapshot — `graph_version` comes back in the body and in
 * `X-Graph-Version`, which is what lets a cached answer be discarded the moment
 * the tree moves (docs/06 §4.5).
 */
export const kinshipApi = {
  derivedRelations: (id: string, params: { kind?: string[]; limit?: number } = {}) =>
    apiFetch<DerivedRelationsResponse>(
      `/persons/${id}/derived-relations${queryString({ limit: 100, ...params })}`,
    ),
  relatives: (id: string, kind: string, params: { degree?: number; removed?: number } = {}) =>
    apiFetch<DerivedRelationsResponse>(
      `/persons/${id}/relatives${queryString({ kind, limit: 100, ...params })}`,
    ),
  ancestors: (id: string, depth = 3) =>
    apiFetch<LineageResponse>(`/persons/${id}/ancestors${queryString({ depth })}`),
  descendants: (id: string, depth = 3) =>
    apiFetch<LineageResponse>(`/persons/${id}/descendants${queryString({ depth })}`),
  /** `mode=all` is the interesting one: endogamy means a pair can be related several ways. */
  path: (from: string, to: string, mode: 'shortest' | 'all' = 'all') =>
    apiFetch<KinshipPathResponse>(`/kinship/path${queryString({ from, to, mode })}`),
};

/**
 * Photos (docs/05 §8). The upload goes through `XMLHttpRequest` rather than
 * `fetch` for one reason: **progress**. A family scanning a shoebox of prints
 * over a slow uplink needs to see a 14 MB file moving; `fetch` has no upload
 * progress event, and a spinner that says nothing for ninety seconds reads as a
 * hang. Everything else here is an ordinary JSON call.
 */
export const photosApi = {
  get: (id: string) => apiFetch<{ photo: PhotoView }>(`/photos/${id}`),
  gallery: (personId: string, params: { filter?: string; limit?: number; cursor?: string } = {}) =>
    apiFetch<GalleryResponse>(`/persons/${personId}/photos${queryString(params)}`),
  update: (id: string, body: { caption?: string | null; taken?: unknown }) =>
    apiFetch<{ photo: PhotoView }>(`/photos/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => apiFetch<void>(`/photos/${id}`, { method: 'DELETE' }),
  tag: (id: string, personId: string, isPrimary = false) =>
    apiFetch<{ photo: PhotoView }>(`/photos/${id}/subjects`, {
      method: 'POST',
      body: { person_id: personId, is_primary: isPrimary },
    }),
  untag: (id: string, personId: string) =>
    apiFetch<void>(`/photos/${id}/subjects/${personId}`, { method: 'DELETE' }),

  upload: (
    file: File,
    fields: { caption?: string; taken?: unknown; subjects?: Array<{ person_id: string }> },
    onProgress?: (fraction: number) => void,
  ): Promise<UploadedPhoto> =>
    new Promise((resolve, reject) => {
      const form = new FormData();
      if (fields.caption) form.append('caption', fields.caption);
      if (fields.taken) form.append('taken', JSON.stringify(fields.taken));
      if (fields.subjects?.length) form.append('subjects', JSON.stringify(fields.subjects));
      // The file goes last: the server reads the parts in order, and having the
      // metadata already in hand when the bytes arrive keeps the handler simple.
      form.append('file', file, file.name);

      const request = new XMLHttpRequest();
      request.open('POST', `${API_BASE}/photos`);
      request.withCredentials = true;
      request.setRequestHeader(CSRF_HEADER, CSRF_HEADER_VALUE);
      // No `content-type` header: the browser must set it, boundary and all.

      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress?.(event.loaded / event.total);
      });
      request.addEventListener('error', () => reject(new ApiError(offlineProblem())));
      request.addEventListener('load', () => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(request.responseText) as unknown;
        } catch {
          parsed = undefined;
        }
        if (request.status >= 200 && request.status < 300) {
          resolve(parsed as UploadedPhoto);
          return;
        }
        reject(
          new ApiError(
            isProblem(parsed)
              ? parsed
              : {
                  type: 'about:blank',
                  title: request.statusText || 'Upload failed',
                  status: request.status,
                  code: 'internal',
                },
          ),
        );
      });
      request.send(form);
    }),
};

/** The chart's bounded neighbourhood (docs/12 M5). The SPA lays it out itself. */
export const treeApi = {
  scene: (focus: string, depth = 4, includePending = false) =>
    apiFetch<TreeScene>(
      `/tree${queryString({ focus, depth, include_pending: includePending ? 'true' : 'false' })}`,
    ),
};

export const relationsApi = {
  create: (body: { relation: unknown; summary?: string }) =>
    apiFetch<CreatedRelation>('/relations', { method: 'POST', body }),
  update: (id: string, body: { changes: Record<string, unknown>; summary?: string }) =>
    apiFetch<{ relation: RelationAssertion; proposal: Proposal | null; bootstrap: boolean }>(
      `/relations/${id}`,
      { method: 'PATCH', body },
    ),
  retire: (id: string, body: { retire_reason: string }) =>
    apiFetch<{ relation: RelationAssertion; proposal: Proposal | null; bootstrap: boolean }>(
      `/relations/${id}`,
      { method: 'DELETE', body },
    ),
};

export interface PersonListParams {
  q?: string;
  gender?: string;
  living_status?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
  [param: string]: string | number | undefined;
}

/**
 * Drops empty params so the api never sees `?q=` and treats it as a filter.
 * Arrays are repeated rather than joined — `?status=open&status=approved` is the
 * shape the `repeatable()` schemas of `packages/shared` parse.
 */
function queryString(params: Record<string, string | number | string[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) if (item !== '') search.append(key, item);
    } else {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}
